/**
 * ImageZoomModal - Modal de zoom d'image avec contrôles +/-
 * Permet de zoomer, dézoomer et naviguer dans une galerie d'images
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

export function ImageZoomModal({
  open,
  onOpenChange,
  imageUrl,
  alt = 'Image',
  showNavigation = false,
  onPrev,
  onNext,
  currentIndex = 0,
  totalImages = 1,
}) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imageRef = useRef(null);

  // Niveaux de zoom
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const ZOOM_STEP = 0.5;

  // Reset zoom quand l'image change ou le modal s'ouvre
  useEffect(() => {
    if (open) {
      resetZoom();
    }
  }, [imageUrl, open]);

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + ZOOM_STEP, MAX_SCALE));
  };

  const handleZoomOut = () => {
    const newScale = Math.max(scale - ZOOM_STEP, MIN_SCALE);
    setScale(newScale);
    if (newScale === 1) {
      setPosition({ x: 0, y: 0 });
    }
  };

  // Gestion du drag pour déplacer l'image zoomée
  const handleMouseDown = (e) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging && scale > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      // Limiter le déplacement pour ne pas perdre l'image
      const maxOffset = (scale - 1) * 200;
      setPosition({
        x: Math.max(-maxOffset, Math.min(maxOffset, newX)),
        y: Math.max(-maxOffset, Math.min(maxOffset, newY))
      });
    }
  }, [isDragging, scale, dragStart]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Zoom avec la molette de la souris
  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Double-clic pour toggle zoom
  const handleDoubleClick = () => {
    if (scale > 1) {
      resetZoom();
    } else {
      setScale(2);
    }
  };

  // Navigation avec les touches du clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          if (showNavigation && onPrev) onPrev();
          break;
        case 'ArrowRight':
          if (showNavigation && onNext) onNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          resetZoom();
          break;
        case 'Escape':
          onOpenChange(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, showNavigation, onPrev, onNext, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] bg-black/95 p-0 border-0 overflow-hidden [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <div 
          ref={containerRef}
          className="relative w-full h-full flex items-center justify-center overflow-hidden"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          {/* Image avec zoom et pan */}
          <img
            ref={imageRef}
            src={imageUrl}
            alt={alt}
            className="max-w-full max-h-full object-contain transition-transform duration-150 select-none"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in'
            }}
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            draggable={false}
          />
          
          {/* Bouton fermer */}
          <button 
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10"
            data-testid="zoom-close-btn"
          >
            <X className="h-6 w-6" />
          </button>
          
          {/* Contrôles de zoom */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/70 rounded-full px-4 py-2 z-10">
            <button
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
              className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="zoom-out-btn"
              title="Dézoomer (-)"
            >
              <ZoomOut className="h-5 w-5" />
            </button>
            
            <div className="text-white text-sm font-medium min-w-[60px] text-center">
              {Math.round(scale * 100)}%
            </div>
            
            <button
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
              className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="zoom-in-btn"
              title="Zoomer (+)"
            >
              <ZoomIn className="h-5 w-5" />
            </button>
            
            {scale > 1 && (
              <button
                onClick={resetZoom}
                className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-colors ml-2"
                data-testid="zoom-reset-btn"
                title="Réinitialiser (0)"
              >
                <RotateCcw className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Flèches de navigation */}
          {showNavigation && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/70 text-white hover:bg-black/90 h-14 w-14 rounded-full flex items-center justify-center transition-colors z-10"
                onClick={() => {
                  resetZoom();
                  onPrev?.();
                }}
                data-testid="zoom-prev-btn"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/70 text-white hover:bg-black/90 h-14 w-14 rounded-full flex items-center justify-center transition-colors z-10"
                onClick={() => {
                  resetZoom();
                  onNext?.();
                }}
                data-testid="zoom-next-btn"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
              
              {/* Indicateur de position */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm z-10">
                {currentIndex + 1} / {totalImages}
              </div>
            </>
          )}
          
          {/* Instructions (affichées brièvement) */}
          {scale === 1 && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-xs text-center">
              Double-cliquez ou utilisez +/- pour zoomer
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImageZoomModal;
